/**
 * Illustration Tiles — front-end behaviour.
 *
 * Turns each block's tile track into a Swiper slider. The Swiper library is
 * loaded once per page as a shared dependency (see EnqueueStylesAndScripts and
 * BlockIllustrationTiles), so this file only wires up the instances and reads
 * `window.Swiper`. Navigation arrows are saved in block markup only when there
 * are more than four tiles; on mobile they are hidden with CSS and the track is
 * swipeable. `watchOverflow` disables sliding when every tile already fits.
 */

const BLOCK_SELECTOR = '.b-illustration-tiles';
const SLIDER_SELECTOR = '.b-illustration-tiles__slider';
const NAV_VISIBLE_AFTER = 4;

/**
 * Initialise a single slider instance.
 *
 * @param {HTMLElement} block The block root element.
 */
const initSlider = ( block ) => {

	const slider = block.querySelector( SLIDER_SELECTOR );

	if ( ! slider || slider.dataset.swiperReady === 'true' ) {

		return;

	}

	const prevEl = block.querySelector( '.b-illustration-tiles__nav-button--prev' );
	const nextEl = block.querySelector( '.b-illustration-tiles__nav-button--next' );
	const slidesCount = slider.querySelectorAll( '.swiper-slide' ).length;
	const hasNav = slidesCount > NAV_VISIBLE_AFTER;

	block.classList.toggle( 'b-illustration-tiles--has-nav', hasNav );

	// eslint-disable-next-line no-new
	new window.Swiper( slider, {
		// Auto-width slides: the tile size is pinned in CSS to the Figma
		// dimensions (mobile 235×275, desktop 361×431), so slides always keep
		// their proportions and the slider just shows more or fewer of them as
		// the viewport changes — they never stretch or exceed the Figma heights.
		slidesPerView: 'auto',
		spaceBetween: 16,
		watchOverflow: true,
		allowTouchMove: true,
		simulateTouch: true,
		mousewheel: {
			enabled: true,
			forceToAxis: true,
			releaseOnEdges: true,
		},
		grabCursor: true,
		threshold: 8,
		a11y: {
			enabled: true,
			prevSlideMessage: 'Previous tile',
			nextSlideMessage: 'Next tile',
		},
		keyboard: {
			enabled: true,
			onlyInViewport: true,
		},
		navigation: ( hasNav && prevEl && nextEl ) ? { prevEl, nextEl } : false,
		breakpoints: {
			// Desktop uses the wider Figma gutter (mobile keeps 16px).
			768: {
				spaceBetween: 24,
			},
		},
	} );

	slider.dataset.swiperReady = 'true';

};

/**
 * Boot all sliders on the page.
 */
const init = () => {

	if ( typeof window.Swiper !== 'function' ) {

		return;

	}

	document.querySelectorAll( BLOCK_SELECTOR ).forEach( initSlider );

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', init );

} else {

	init();

}
